import i18nPackage from "../../i18n/i18n";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import styles from "./index.module.css";
import StepDescription, { StepWrapper } from "./CommonStepComponents";
import { useEffect, useMemo } from "react";
import { sortPresetProfiles } from "../profile/profileHelpers";
import ProfileList, { PresetProfileGrid, ProfileListProps } from "../profile/ProfileList";
import { CoreSkeletonWrapper } from "../../core";
import { isEnforcedProfile } from "@verity/proctoring-option";

type ProfileStepProps = {
	activeProfile: ProctoringOptionProfileV2 | undefined;
	onProfileSelect: (profileId: number | undefined) => void;
	loading?: boolean;
	showPresetProfile: boolean;
	isCustomConfig: boolean; // Theres probably a more elegant way of doing this
} & Pick<
	ProfileListProps,
	| "canDeleteProfile"
	| "onDelete"
	| "proctoringData"
	| "verityInstituteSettings"
	| "optionRelatedInstituteSettings"
	| "enforcedProfilesOnly"
	| "profiles"
>;

export default function ProfileStep({
	profiles,
	activeProfile,
	onProfileSelect,
	loading,
	enforcedProfilesOnly,
	verityInstituteSettings,
	optionRelatedInstituteSettings,
	proctoringData,
	showPresetProfile,
	canDeleteProfile,
	onDelete,
	isCustomConfig,
}: ProfileStepProps) {
	const presetProfiles = useMemo(() => sortPresetProfiles(profiles), [profiles]);

	// If first time creating a quiz, auto select either the first preset or the first profile
	useEffect(() => {
		const isEnforcedAndInvalid =
			enforcedProfilesOnly && ((activeProfile && !isEnforcedProfile(activeProfile)) || isCustomConfig);

		// Only return early if theres an active profile or its a custom config AND there is no issues with enforce profiles
		if ((activeProfile || isCustomConfig) && !isEnforcedAndInvalid) {
			return;
		}

		// Select the first preset
		if (showPresetProfile && presetProfiles.length !== 0) {
			onProfileSelect(presetProfiles[0].id);
		}

		// If there are profiles select the first one
		else if (!showPresetProfile && profiles.length > 0) {
			onProfileSelect(profiles[0].id);
		}
	}, []);

	return (
		<StepWrapper>
			<div className={styles.profileStep}>
				<StepDescription loading={loading}>
					{enforcedProfilesOnly
						? i18nPackage.t("editQuizPage.profileStep.institutionRequiredProfiles")
						: i18nPackage.t("editQuizPage.profileStep.description")}
				</StepDescription>
				<CoreSkeletonWrapper loading={loading} height={"100%"}>
					{showPresetProfile && !enforcedProfilesOnly && presetProfiles.length > 0 ? (
						<PresetProfileGrid
							liteMode={verityInstituteSettings.liteMode}
							presetProfiles={presetProfiles}
							onSelect={onProfileSelect}
							selectedId={activeProfile?.id}
						/>
					) : (
						<ProfileList
							enforcedProfilesOnly={enforcedProfilesOnly}
							optionRelatedInstituteSettings={optionRelatedInstituteSettings}
							proctoringData={proctoringData}
							profiles={profiles}
							selectedId={activeProfile?.id}
							onProfileSelected={onProfileSelect}
							verityInstituteSettings={verityInstituteSettings}
							showCustomButton={true}
							canDeleteProfile={canDeleteProfile}
							onDelete={onDelete}
						/>
					)}
				</CoreSkeletonWrapper>
			</div>
		</StepWrapper>
	);
}
